'use client';

import { useState, useEffect, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { useAuth } from '../../../hooks/useAuth';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Separator } from '@/app/components/ui/separator';
import { Github, Mail, Loader2, AlertCircle, Eye, EyeOff, Sparkles } from 'lucide-react';
import { Alert, AlertDescription } from '@/app/components/ui/alert';

function LoginPageContent() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const { login, user, loading: authLoading } = useAuth();
  const searchParams = useSearchParams();
  const returnUrl = searchParams.get('return_url') || '/dashboard';
  const state = searchParams.get('state');

  // Redirect if user is already authenticated
  useEffect(() => {
    if (!authLoading && user) {
      const redirectUrl = returnUrl.startsWith('http') || returnUrl.includes('.')
        ? returnUrl
        : `${window.location.origin}${returnUrl.startsWith('/') ? returnUrl : `/${returnUrl}`}`;
      window.location.href = redirectUrl;
    }
  }, [authLoading, user, returnUrl]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await login(email, password);
      // Full page redirect ensures cookies are sent and middleware sees the session
      const redirectUrl = returnUrl.startsWith('http') || returnUrl.includes('.')
        ? returnUrl
        : `${window.location.origin}${returnUrl.startsWith('/') ? returnUrl : `/${returnUrl}`}`;
      window.location.href = redirectUrl;
    } catch (error: any) {
      setError(error.message || 'Login failed');
      setLoading(false);
    }
  };

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin mx-auto" />
          <p className="text-muted-foreground">Checking authentication...</p>
          {process.env.NODE_ENV === 'development' && (
            <div className="text-xs text-gray-500">
              <p>Auth Loading: {authLoading ? 'true' : 'false'}</p>
              <p>User: {user ? 'authenticated' : 'not authenticated'}</p>
              <p>Return URL: {returnUrl}</p>
              <p>State: {state}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Don't render the form if user is already authenticated (will redirect)
  if (user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin mx-auto" />
          <p className="text-muted-foreground">Redirecting...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-linear-to-br from-background via-background to-primary/5 p-4">
      <div className="absolute inset-0 bg-grid-pattern opacity-[0.02] pointer-events-none"></div>
      <Card className="w-full max-w-md animate-fade-in shadow-2xl border-primary/10 relative overflow-hidden">
        <div className="absolute inset-0 bg-linear-to-br from-primary/5 via-transparent to-transparent pointer-events-none"></div>
        <CardHeader className="space-y-3 relative">
          <div className="mx-auto w-16 h-16 rounded-full bg-linear-to-br from-primary to-primary/70 flex items-center justify-center shadow-lg animate-pulse-glow">
            <Sparkles className="h-8 w-8 text-primary-foreground" />
          </div>
          <CardTitle className="text-3xl font-bold text-center bg-linear-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">Welcome back</CardTitle>
          <CardDescription className="text-center text-base">
            Sign in to continue your journey
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6 relative">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2 group">
              <Label htmlFor="email" className="text-sm font-medium">Email address</Label>
              <Input
                id="email"
                type="email"
                placeholder="john@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-11 transition-all duration-200 border-2 focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
            </div>
            <div className="space-y-2 group">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-sm font-medium">Password</Label>
                <Link href="/auth/forgot-password" className="text-xs text-primary hover:underline transition-colors">
                  Forgot?
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-11 pr-10 transition-all duration-200 border-2 focus:border-primary focus:ring-2 focus:ring-primary/20"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <Button type="submit" className="w-full h-11 text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-200 hover:-translate-y-0.5" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  <Mail className="mr-2 h-5 w-5" />
                  Sign in
                </>
              )}
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <Separator className="w-full" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-3 text-muted-foreground font-medium">Or continue with</span>
            </div>
          </div>

          <div className="space-y-3">
            <Button
              variant="outline"
              className="w-full h-11 border-2 hover:bg-accent hover:border-primary/50 transition-all duration-200"
              onClick={() => {/* OAuth will be implemented later */}}
            >
              <Github className="mr-2 h-5 w-5" />
              Continue with GitHub
            </Button>
            
            {process.env.NODE_ENV === 'development' && (
              <Button
                variant="outline"
                className="w-full mt-2"
                onClick={async () => {
                  if (process.env.NODE_ENV === 'development') {
                    // Fetch test credentials from a secure endpoint
                    const testCreds = await fetch('/api/dev/test-credentials').then(r => r.json());
                    setEmail(testCreds.email);
                    setPassword(testCreds.password);
                  }
                }}
              >
                Fill Test Credentials (Dev Only)
              </Button>
            )}
          </div>

          <div className="text-center pt-4 border-t">
            <p className="text-sm text-muted-foreground">
              Don't have an account?{' '}
              <Link
                href={`/auth/signup${returnUrl !== '/dashboard' ? `?return_url=${encodeURIComponent(returnUrl)}${state ? `&state=${encodeURIComponent(state)}` : ''}` : ''}`}
                className="text-primary font-semibold hover:underline transition-colors"
              >
                Sign up for free
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    }>
      <LoginPageContent />
    </Suspense>
  );
}
